import React, { useEffect, useRef, useState } from 'react';
import { closingLabelInline } from '../vocabulary/learningVocabulary';

const MIN_REASON_LENGTH = 10;

interface Props {
  /** Milestone sequence being disputed; 8 is the closing milestone. */
  sequence: number;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

/**
 * Raising a dispute used to be a bare `window.prompt` reading
 * "Razon de la disputa (minimo 10 caracteres)". QA's note was simply "I don't
 * understand the option to dispute pending sessions" — nothing said what a
 * dispute does, when it can be raised, or what happens to the money.
 *
 * The consequence is real: an open dispute pauses escrow release for that
 * milestone until an admin resolves it, so the parent deserves to know that
 * before committing.
 */
const DisputeDialog: React.FC<Props> = ({ sequence, busy = false, onCancel, onConfirm }) => {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const trimmed = reason.trim();
  const tooShort = trimmed.length < MIN_REASON_LENGTH;
  const label = sequence === 8 ? closingLabelInline() : `la sesión ${sequence}`;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (tooShort || busy) return;
    onConfirm(trimmed);
  };

  return (
    <div
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(22,30,34,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="dispute-title"
        style={{
          background: '#fff',
          borderRadius: 10,
          padding: 24,
          maxWidth: 480,
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
        }}
      >
        <h2 id="dispute-title" style={{ margin: '0 0 8px', fontSize: 20 }}>
          Reportar un problema con {label}
        </h2>

        <p style={{ color: '#444', fontSize: 14, margin: '0 0 12px' }}>
          Úsalo si la sesión no ocurrió, terminó antes de tiempo, o no correspondió a lo ofrecido.
        </p>

        <div
          style={{
            background: '#fff8e6',
            border: '1px solid #f5a623',
            borderRadius: 8,
            padding: '12px 14px',
            marginBottom: 16,
          }}
        >
          <strong style={{ display: 'block', fontSize: 13, color: '#7a4d06', marginBottom: 6 }}>
            Qué pasa cuando reportas
          </strong>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#7a4d06' }}>
            <li>El pago de {label} queda retenido y no se libera al profesor.</li>
            <li>Nuestro equipo revisa el caso y te contacta.</li>
            <li>El resto de tus sesiones continúa con normalidad.</li>
          </ul>
        </div>

        <form onSubmit={submit}>
          <label
            htmlFor="dispute-reason"
            style={{
              display: 'block',
              fontSize: 13,
              fontWeight: 600,
              color: '#444',
              marginBottom: 6,
            }}
          >
            ¿Qué pasó?
          </label>
          <textarea
            id="dispute-reason"
            ref={textareaRef}
            className="form-input"
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder="Cuéntanos con detalle qué ocurrió en esta sesión."
            style={{ resize: 'vertical', width: '100%' }}
            aria-describedby="dispute-help"
          />
          <div id="dispute-help" className={touched && tooShort ? 'form-error' : 'form-help'}>
            {touched && tooShort
              ? `Escribe al menos ${MIN_REASON_LENGTH} caracteres para que podamos revisarlo.`
              : `Mínimo ${MIN_REASON_LENGTH} caracteres.`}
          </div>

          <div
            style={{
              display: 'flex',
              gap: 10,
              marginTop: 20,
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
            }}
          >
            <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-error" disabled={busy || tooShort}>
              {busy ? 'Enviando…' : 'Reportar problema'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DisputeDialog;
